import { useEffect, useState } from 'react';
import SurveyButton from '@/components/Base/SurveyButton';
import SurveyValidationAlert from '@/components/survey/SurveyValidationAlert';
import SurveyBackgroundLayout from '@/components/layout/SurveyBackgroundLayout';
import { SurveyContentPanel, SurveyContentPanelInset, SurveyQuestionPanel } from '@/components/layout/SurveyContentPanel';
import { useSurveyLanguage } from '@/hooks/useSurveyLanguage';
import { useTranslation } from '@/hooks/useTranslation';
import { useSurveyRequiredAnswers } from '@/hooks/useSurveyRequiredAnswers';
import { useAppDispatch, useAppSelector } from '@/stores/hooks';
import { setSurveyScreeningAnswers } from '@/stores/appSlice';
import { selectSurveyScreening } from '@/stores/selectors';
import { Navigate, useNavigate } from 'react-router-dom';

const AGE_BAND_LABELS = ['<19', '19-24', '25-34', '35-44', '45-55', '55>'] as const;

type AgeBand = (typeof AGE_BAND_LABELS)[number];

const SMOKER_OPTIONS = ['yes', 'no'] as const;
type SmokerChoice = (typeof SMOKER_OPTIONS)[number];

function ageBandFromStored(screening: { ageBand: string } | null): AgeBand | null {
  if (!screening) return null;
  return AGE_BAND_LABELS.includes(screening.ageBand as AgeBand) ? (screening.ageBand as AgeBand) : null;
}

export default function AgeSmoker() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { surveyLanguage } = useSurveyLanguage();
  const { t } = useTranslation();
  const screening = useAppSelector(selectSurveyScreening);
  const { warning, canProceed, dismissWarning } = useSurveyRequiredAnswers();
  const [selectedBand, setSelectedBand] = useState<AgeBand | null>(() => ageBandFromStored(screening));
  const [smokerChoice, setSmokerChoice] = useState<SmokerChoice | null>(() => screening?.smoker ?? null);

  useEffect(() => {
    if (screening == null) return;
    setSelectedBand(ageBandFromStored(screening));
    setSmokerChoice(screening.smoker);
  }, [screening]);

  useEffect(() => {
    dismissWarning();
  }, [selectedBand, smokerChoice, dismissWarning]);

  const handleSubmit = () => {
    const allAnswered = selectedBand != null && smokerChoice != null;
    if (!canProceed(allAnswered, t.ageSmoker.allRequiredWarning)) return;

    if (selectedBand === '<19' || smokerChoice === 'no') {
      navigate('/thankyou');
      return;
    }

    dispatch(setSurveyScreeningAnswers({ ageBand: selectedBand, smoker: smokerChoice }));
    navigate('/pdpa');
  };

  if (surveyLanguage == null) {
    return <Navigate to="/" replace />;
  }

  return (
    <SurveyBackgroundLayout>
      <div className="absolute z-10 inset-x-0 top-[10%] flex flex-col items-center text-center">
        <h1 className="text-[36px] font-semibold leading-tight tracking-tight text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.4)] sm:text-[42px]">
          {t.ageSmoker.title}
        </h1>
      </div>

      <div className="relative z-10 flex flex-col pt-[calc(12dvh+3.75rem)] pb-12">
        <SurveyContentPanelInset>
          <SurveyContentPanel className="!p-3">
            <div className="grid grid-cols-2 gap-2">
              {AGE_BAND_LABELS.map((label) => (
                <SurveyButton
                  key={label}
                  variant="choice"
                  selected={selectedBand === label}
                  onClick={() => setSelectedBand(label)}
                >
                  {label}
                </SurveyButton>
              ))}
            </div>
          </SurveyContentPanel>
        </SurveyContentPanelInset>

        <SurveyContentPanelInset className="mt-6">
          <SurveyQuestionPanel className="!mb-4">
            <h2 className="m-0 text-center text-[32px] font-semibold leading-tight text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.4)] sm:text-[36px]">
              {t.ageSmoker.smokerQuestion}
            </h2>
          </SurveyQuestionPanel>
        </SurveyContentPanelInset>

        <SurveyContentPanelInset>
          <SurveyContentPanel className="!p-3">
            <div className="grid grid-cols-2 gap-2">
              {SMOKER_OPTIONS.map((id) => (
                <SurveyButton
                  key={id}
                  variant="choice"
                  selected={smokerChoice === id}
                  onClick={() => setSmokerChoice(id)}
                >
                  {id === 'yes' ? t.ageSmoker.yes : t.ageSmoker.no}
                </SurveyButton>
              ))}
            </div>
          </SurveyContentPanel>
        </SurveyContentPanelInset>

        <div className="mt-10 flex flex-col items-center gap-3">
          {warning != null ? <SurveyValidationAlert>{warning}</SurveyValidationAlert> : null}
          <SurveyButton onClick={handleSubmit}>{t.ageSmoker.submit}</SurveyButton>
        </div>
      </div>
    </SurveyBackgroundLayout>
  );
}
